import { NgTemplateOutlet } from '@angular/common';
import type { ElementRef } from '@angular/core';
import { ChangeDetectorRef, Component, ViewChild, inject } from '@angular/core';

import type { Node as ProsemirrorNode, NodeType } from 'prosemirror-model';
import type { EditorView, NodeView } from 'prosemirror-view';
import { Subject } from 'rxjs';

import type { AlignValue, ImageNodeView, ImageNodeAttrs } from '@core/prosemirror-legacy';
import { ImageNode } from '@core/prosemirror-legacy';
import type { ProsemirrorAngularComponent } from '@core/prosemirror-legacy/angular';
import { getImageDimensionsFormUrl } from '@core/wysiwyg-legacy';

@Component({
  templateUrl: 'image-node.component.html',
  styleUrls: ['./image-node.component.less'],
  imports: [NgTemplateOutlet],
})
export class ImageNodeComponent implements ProsemirrorAngularComponent, ImageNodeView {
  private readonly cdRef = inject(ChangeDetectorRef);

  private readonly MIN_WIDTH = 20;

  public isEditable: boolean = false;
  public src: string;
  public width: number;
  public alt = '';
  public caption = '';
  public href = '';
  public targetBlank = false;
  public align?: AlignValue;
  public htmlClass?: string;
  public figureStyle: string;

  @ViewChild('dom', { static: true }) public dom: ElementRef<HTMLElement>;
  public pmOnDestroy$ = new Subject<void>();

  private nodeSpec: NodeType;
  private ngOnDestroy$ = new Subject<void>();
  private node: ProsemirrorNode;
  private view: EditorView;
  private getPos: () => number;
  private onResize: (newWidth: number, position: number) => void;

  public get imgStyle() {
    return this.width ? `width:${this.width}px;` : '';
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public getNodeView(
    node: ProsemirrorNode,
    view: EditorView,
    getPos: () => number,
    data: { isEditable: boolean; nodeType: NodeType; onResize: (newWidth: number) => void },
  ): NodeView {
    this.isEditable = data.isEditable;
    this.view = view;
    this.nodeSpec = data.nodeType;
    this.getPos = getPos;
    this.onResize = data.onResize;
    this.update(node);

    return {
      dom: this.dom.nativeElement,
      update: (node: ProsemirrorNode) => this.updateView(node),
      destroy: () => this.pmOnDestroy$.next(),
    };
  }

  public initResize(mousedownEvent: MouseEvent) {
    if (!this.isEditable) return;
    mousedownEvent.preventDefault();
    mousedownEvent.stopPropagation();

    const defaultCursor = document.body.style.cursor;
    const originX = mousedownEvent.clientX;
    const initialWidth = this.width;
    document.body.style.cursor = 'nwse-resize';

    const resize = (moveEvent: MouseEvent) => {
      moveEvent.preventDefault();
      moveEvent.stopPropagation();
      const dx = originX - moveEvent.clientX;
      this.width = Math.max(initialWidth - dx, this.MIN_WIDTH);
      this.cdRef.detectChanges();
    };

    const finalize = () => {
      document.removeEventListener('mousemove', resize);
      document.removeEventListener('mouseup', finalize);

      document.body.style.cursor = defaultCursor;
      if (this.width !== initialWidth) {
        this.onResize(this.width, this.getPos());
      }
    };

    document.addEventListener('mousemove', resize);
    document.addEventListener('mouseup', finalize);
  }

  private updateView(node: ProsemirrorNode) {
    if (ImageNode.is(node)) {
      this.update(node);
      return true;
    }

    return false;
  }

  private update(node: ProsemirrorNode) {
    const { src, width, alt, caption, href, targetBlank, align } = <ImageNodeAttrs>node.attrs;
    const html_class = node.attrs['html-class'];
    this.node = node;
    this.src = src;
    if (!width && !this.width) {
      this.initWidth(src);
    } else if (width) {
      this.width = width;
    }
    this.alt = alt;
    this.caption = caption || '';
    this.href = href;
    this.targetBlank = targetBlank;
    this.align = align || undefined;
    this.htmlClass = html_class || undefined;
    this.setInlineStyles();

    this.cdRef.detectChanges();
  }

  private initWidth(src: string) {
    getImageDimensionsFormUrl(src).subscribe(({ width }) => {
      this.width = width;
      this.cdRef.detectChanges();
    });
  }

  private setInlineStyles() {
    if (this.nodeSpec.spec.alignExtension) {
      this.figureStyle = this.nodeSpec.spec.alignExtension.getStyleSerialization(this.node) || '';
    }
  }
}
